import { Paginator, Message } from '@twilio/conversations';
import { ToastrService } from 'ngx-toastr';
import { Observable, from, throwError } from 'rxjs';
import { tap, filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { SnCurrentUser } from '@lib/auth';
import { t } from '@lib/translation';

import { SnChannel } from '../models';
import { SnChatService } from './chat.service';

@Injectable({ providedIn: 'root' })
export class SnMessagesService {
  constructor(
    private readonly _chatService: SnChatService,
    private readonly _toastrService: ToastrService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _sanitizer: DomSanitizer,
  ) {}

  public getMessages(channel: SnChannel): Observable<Paginator<Message>> {
    return from(channel.twilioChannel.getMessages(30));
  }

  public sendTextMessage(channel: SnChannel, message: string): Observable<number> {
    const sanitizedMessage = this._sanitizer.sanitize(SecurityContext.HTML, message) ?? '';
    return this._sendMessage(channel, sanitizedMessage).pipe(
      filter((data) => typeof data === 'number'),
      tap((index: number) => {
        channel.twilioChannel.getUnreadMessagesCount();
      }),
    );
  }

  public sendMediaMessage(
    channel: SnChannel,
    file: File,
    description?: string,
  ): Observable<number> {
    if (!channel.twilioChannel) {
      return throwError(t('communication.chat.errors.twilioChannelUndefined'));
    }
    const sendMediaOptions = {
      contentType: file.type,
      filename: file.name,
      media: file,
    };
    const attribute: any = {};
    attribute.authorFriendlyName = this._chatService.ownTwilioUser.friendlyName;
    attribute.authorCallSign = this._currentUser.callSign;
    const body = description
      ? this._sanitizer.sanitize(SecurityContext.HTML, description) ?? ''
      : '';
    const request$ = from(
      channel.twilioChannel
        .prepareMessage()
        .setBody(body)
        .setAttributes(attribute)
        .addMedia(sendMediaOptions)
        .build()
        .send(),
    );
    return request$.pipe(
      tap({
        next: () => {
          this._toastrService.success(t('communication.chat.messages.mediaSentSuccessfully'));
        },
        error: () => {
          this._toastrService.error(t('communication.chat.errors.sendMedia'));
        },
      }),
      filter((data) => typeof data === 'number'),
      tap((index: number) => {
        channel.twilioChannel.getUnreadMessagesCount();
      }),
    );
  }

  public shareMediaMessage(channel: SnChannel, cameraName: string, data: any): Observable<number> {
    return this._sendMessage(
      channel,
      t('communication.chat.messages.sharedCamera', {
        userName: this._chatService.ownTwilioUser.friendlyName,
        cameraName,
      }),
      data,
    ).pipe(
      filter((dataShare) => typeof dataShare === 'number'),
      tap((index: number) => {
        channel.twilioChannel.getUnreadMessagesCount();
      }),
    );
  }

  public sendMultiCamerasMessage(channel: SnChannel, data: any): Observable<number> {
    return this._sendMessage(
      channel,
      t('communication.chat.messages.sharedCamerasForIncident', { incidentName: channel.name }),
      data,
    ).pipe(
      filter((dataShare) => typeof dataShare === 'number'),
      tap((index: number) => {
        channel.twilioChannel.getUnreadMessagesCount();
      }),
    );
  }

  private _sendMessage(
    channel: SnChannel,
    message: string | FormData,
    attribute?: any,
  ): Observable<number | Object> {
    attribute = attribute || {};
    attribute.authorFriendlyName = this._chatService.ownTwilioUser.friendlyName;
    attribute.authorCallSign = this._currentUser.callSign;
    return from(channel?.twilioChannel?.sendMessage(message, attribute));
  }
}
